import type {
  RowSelectionState,
  OnChangeFn,
  PaginationState,
  VisibilityState,
  Updater,
  ColumnOrderState
} from '@tanstack/react-table';
import React, { useState, useMemo, useRef } from 'react';

import useInfiniteScroll from './_useInfiniteScroll';

import { TableExperimentalPaginationState } from '.';

type FetchResponse = {
  totalFetched: number;
  totalFetchAvailable: number;
};

type UseTable = {
  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;
  pagination: PaginationState;
  setPagination: (updater: Updater<PaginationState>) => void;
  ref: React.MutableRefObject<HTMLTableElement | undefined>;
  isFetching: boolean;
  columnVisibility: VisibilityState;
  setColumnVisibility: (updater: Updater<VisibilityState>) => void;
  columnOrder: ColumnOrderState;
  setColumnOrder: (updater: Updater<ColumnOrderState>) => void;
};

type UseTableProps = {
  initialPageIndex?: number;
  initialPageSize?: number;
  fetchData?: () => FetchResponse | Promise<FetchResponse>;
  enableInfiniteScroll?: boolean;
};

const useTable = (props: UseTableProps = {}): UseTable => {
  const { initialPageIndex, initialPageSize, fetchData, enableInfiniteScroll } = props;
  const ref = useRef<HTMLTableElement>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>([]);

  const [{ pageIndex, pageSize }, setPagination] = useState<TableExperimentalPaginationState>({
    pageIndex: initialPageIndex ?? 0,
    pageSize: initialPageSize ?? 10
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize
    }),
    [pageIndex, pageSize]
  );

  const { isFetching } = useInfiniteScroll({ ref, fetchData, enableInfiniteScroll });

  return {
    rowSelection,
    setRowSelection,
    pagination,
    setPagination,
    ref,
    isFetching,
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder
  };
};

export default useTable;
